import { useState } from 'react';
import { flushSync } from 'react-dom';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useIntervalSwitchPreview, useSwitchToAnnual } from '../../hooks/useBilling';
import { ApiRequestError } from '../../api/errors';
import { formatDateShort } from '../../utils/date';
import type { UpgradeOption } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  option: UpgradeOption;
}

// Plan-row prices: trim trailing zeros (matches UpgradePlanModal/AddSubscriptionModal)
function formatPlanPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Charge amounts: always two decimals
function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

export default function IntervalSwitchModal({ isOpen, onClose, option }: Props) {
  const [priceChanged, setPriceChanged] = useState(false);
  const [isVerifyingPrice, setIsVerifyingPrice] = useState(false);
  // True from the moment the charge mutation is fired until it errors (reset
  // for retry) — see the flushSync call in handleConfirm for why this can't
  // just be derived from `switchToAnnual.isPending`.
  const [chargeInFlight, setChargeInFlight] = useState(false);
  const switchToAnnual = useSwitchToAnnual();

  const result = switchToAnnual.isSuccess ? switchToAnnual.data : undefined;
  const isComplete = result !== undefined;

  // possible_desync: the switch may have partially applied server-side and
  // the team was alerted — re-confirming would replay the same outcome
  const isDesync =
    switchToAnnual.error instanceof ApiRequestError &&
    switchToAnnual.error.slug === 'possible_desync';

  // Preview must stop polling no later than the moment the charge mutation
  // succeeds (see useSwitchToAnnual's hard-requirement doc comment): once
  // `['billing']` is invalidated on success, an *enabled* preview query
  // would refetch straight into a 400 (the subscription is annual now).
  //
  // Gating on `switchToAnnual.isPending` looks appealing (it flips true the
  // instant `.mutate()` is called) but does NOT reliably win this race:
  // verified against actual @tanstack/query-core internals plus a live
  // instrumented test run, the mutation's hook-level onSuccess (which does
  // the invalidateQueries call) can run to completion, and the mutation can
  // dispatch all the way to 'success', inside a single microtask burst that
  // React never surfaces as an intermediate `isPending: true` render — so a
  // flag derived from `isPending` can still be `true` (query still enabled)
  // at the exact moment invalidateQueries decides which queries are active.
  // `chargeInFlight` is instead set via `flushSync` in handleConfirm, which
  // forces the disabled `enabled` option onto this query's observer
  // *synchronously*, before `.mutate()` is ever called — no race is possible.
  //
  // Also gated on `!isDesync`: `chargeInFlight` resets to `false` on any
  // mutate-level `onError` (including possible_desync) so a normal payment
  // failure can resume polling for a retry — but a desync means "don't
  // retry, ever" (confirm is disabled/replaced), so without this the resumed
  // poll would refetch, 400, and stack a second error box under the desync
  // message.
  const preview = useIntervalSwitchPreview(
    option.subscription_id,
    isOpen && !isComplete && !isDesync && !chargeInFlight
  );

  const handleClose = () => {
    if (switchToAnnual.isPending || isVerifyingPrice) return;
    switchToAnnual.reset();
    setPriceChanged(false);
    setChargeInFlight(false);
    onClose();
  };

  const handleConfirm = async () => {
    // Re-verify the quoted charge right before charging — the preview can go
    // stale while the modal sits open (e.g. Stripe's upcoming-invoice amount
    // drifts as the renewal date approaches / passes)
    const shownAmount = preview.data?.amount_due_today;
    setPriceChanged(false);
    setIsVerifyingPrice(true);
    try {
      const fresh = await preview.refetch();
      if (fresh.error || !fresh.data) {
        // Preview error box renders via preview.isError; don't charge
        return;
      }
      if (
        shownAmount === undefined ||
        Math.abs(fresh.data.amount_due_today - shownAmount) > 0.01
      ) {
        // Amount drifted from what the customer saw — the preview box has
        // re-rendered with the new amount; require an explicit re-confirm
        setPriceChanged(true);
        return;
      }
    } finally {
      setIsVerifyingPrice(false);
    }

    // Disable the preview query synchronously — via flushSync — *before*
    // firing the charge mutation. See the comment on `preview` above: this
    // must be synchronous, not a post-hoc flag flipped from onSuccess/
    // onError, or the mutation's own invalidateQueries(['billing']) call can
    // beat it and refetch this still-enabled query into a 400.
    flushSync(() => setChargeInFlight(true));

    switchToAnnual.mutate(option.subscription_id, {
      // Charge failed (e.g. card declined) — re-enable the preview so the
      // customer can retry with a live quote.
      onError: () => setChargeInFlight(false),
    });
  };

  const submitDisabled =
    switchToAnnual.isPending ||
    isVerifyingPrice ||
    isDesync ||
    !preview.data ||
    preview.isError;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Switch to Annual Billing">
      {isComplete && result ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800">
              You were charged{' '}
              <span className="font-medium">
                {formatMoney(result.amount_charged, result.currency)}
              </span>
              .{' '}
              {result.new_renewal_date ? (
                <>
                  Your renewal date is now{' '}
                  <span className="font-medium">
                    {formatDateShort(result.new_renewal_date)}
                  </span>
                  .
                </>
              ) : (
                "You're now on annual billing."
              )}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-gray-700">
            Switch <span className="font-medium">{option.hostname}</span> from
            monthly to annual billing.
          </p>

          <div className="space-y-2">
            {option.interval_switch?.annual_price !== undefined &&
              option.interval_switch.currency && (
                <p className="text-sm text-gray-700">
                  {option.current_plan} annual price:{' '}
                  <span className="font-medium text-gray-900">
                    {`${formatPlanPrice(
                      option.interval_switch.annual_price,
                      option.interval_switch.currency
                    )}/year`}
                  </span>
                </p>
              )}
            {option.interval_switch?.dip_annual_price !== undefined &&
              option.interval_switch.currency && (
                <p className="text-sm text-gray-700">
                  Dedicated IP:{' '}
                  <span className="font-medium text-gray-900">
                    {`${formatPlanPrice(
                      option.interval_switch.dip_annual_price,
                      option.interval_switch.currency
                    )}/year`}
                  </span>
                </p>
              )}
          </div>

          {preview.isPending ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="sm" />
              <span>Calculating your annual billing charge...</span>
            </div>
          ) : preview.isError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">
                {preview.error instanceof ApiRequestError
                  ? // 400 eligibility reason from the API (see
                    // previewIntervalSwitch) — e.g. "in its credit period,
                    // contact support" — is written for the customer
                    preview.error.message
                  : 'Failed to calculate the annual billing charge. Please try again or contact support.'}
              </p>
            </div>
          ) : preview.data ? (
            <>
              <p className="text-sm text-gray-700">
                Due today:{' '}
                <span className="font-medium text-gray-900">
                  {formatMoney(
                    preview.data.amount_due_today,
                    preview.data.currency
                  )}
                </span>{' '}
                — annual price minus credit for the unused part of your
                current month.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-900 font-medium">
                  {preview.data.new_renewal_date ? (
                    <>
                      Your renewal date moves to{' '}
                      <span className="font-semibold">
                        {formatDateShort(preview.data.new_renewal_date)}
                      </span>{' '}
                      — you'll be billed annually from today.
                    </>
                  ) : (
                    "You'll be billed annually from today."
                  )}
                </p>
              </div>
            </>
          ) : null}

          {priceChanged && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                The charge amount has been updated — please review and
                confirm again.
              </p>
            </div>
          )}

          {switchToAnnual.isError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">
                {switchToAnnual.error instanceof Error
                  ? switchToAnnual.error.message
                  : 'Failed to switch to annual billing. Please try again or contact support.'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleClose}
              disabled={switchToAnnual.isPending || isVerifyingPrice}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitDisabled}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDesync ? (
                'Switch pending review'
              ) : switchToAnnual.isPending || isVerifyingPrice ? (
                <>
                  <Spinner size="sm" className="border-white border-t-transparent" />
                  <span>Processing...</span>
                </>
              ) : preview.data ? (
                `Confirm & Pay ${formatMoney(preview.data.amount_due_today, preview.data.currency)}`
              ) : (
                'Confirm & Pay'
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
